import React from 'react';
import Modal from '../../../../components/shareable/modal';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';

const CustomerViewModal = ({ isOpen, onClose, customerData }) => {
  if (!customerData) return null;

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format discount percentage
  const formatDiscount = (discount) => {
    return discount ? `${discount}%` : '';
  };

  // Field labels for display
  const fieldLabels = {
    customer: 'Customer Name',
    address: 'Address',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    standard_discount: 'Standard Discount',
    primary_contact: 'Primary Contact',
    title: 'Title',
    phone: 'Phone',
    email: 'Email',
    notes: 'Notes'
  };

  // Group fields into rows of 3
  const orderedFields = [
    'customer', 'primary_contact', 'email',
    'phone', 'address', 'address2',
    'city', 'state', 'zip',
    'title', 'standard_discount'
  ];

  const fieldGroups = [];
  for (let i = 0; i < orderedFields.length; i += 3) {
    fieldGroups.push(orderedFields.slice(i, i + 3));
  }

  return (
    <Modal isOpen={isOpen} width='3xl' onClose={onClose}>
      <div className="flex flex-col h-full">
        <h2 className="text-xl font-semibold mb-4 text-red-700">Customer Details</h2>
        
        {/* Scrollable content */}
        <div className="flex-grow overflow-y-auto max-h-96">
          <div className="space-y-4">
            {fieldGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="grid grid-cols-3 gap-4">
                {group.map((field) => (
                  <div key={field} className="mb-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {fieldLabels[field]}
                    </label>
                    <div className="p-2 bg-gray-50 text-sm min-h-[38px]">
                      {field === 'standard_discount' 
                        ? formatDiscount(customerData[field])
                        : field === 'phone'
                        ? formatPhoneNumber(customerData[field])
                        : customerData[field] || ''}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            
            {/* Notes section */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {fieldLabels.notes}
              </label>
              <div className="p-4 bg-white rounded-lg shadow-md border border-gray-100 min-h-[100px]">
                {customerData.notes && customerData.notes.length > 0 ? (
                  <div className="space-y-4">
                    {customerData.notes.map((note, index) => (
                      <div key={index} className="relative">
                        {/* Divider between notes (except for the last one) */}
                        {index < customerData.notes.length - 1 && (
                          <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-100"></div>
                        )}
                        <div className="pb-4">
                          <div className="border-l-4 border-red-600 pl-4 py-3 bg-white rounded-r-lg shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <div className="text-xs font-medium text-gray-700">
                                By: {note.user}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatDate(note.date)}
                              </div>
                            </div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                              {note.text}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic py-2 text-center">No notes available</div>
                )}
              </div>
            </div>
            
            {/* Additional Locations Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-md font-medium text-gray-700 mb-3">Additional Locations</h3>
              
              {customerData.additional_locations && customerData.additional_locations.length > 0 ? (
                <div className="space-y-3">
                  {customerData.additional_locations.map((location, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-800">Location {index + 1}</h4>
                      </div>
                      
                      {/* Location field - full width */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                        <div className="p-2 bg-white text-sm min-h-[60px]">
                          {location.location || ''}
                        </div>
                      </div>
                      
                      {/* Second row with contact fields */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Primary Contact</label>
                          <div className="p-2 bg-white text-sm min-h-[36px]">
                            {location.primary_contact || ''}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Contact Number</label>
                          <div className="p-2 bg-white text-sm min-h-[36px]">
                            {formatPhoneNumber(location.primary_contact_number) || ''}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                          <div className="p-2 bg-white text-sm min-h-[36px]"> {location.email || ''} </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic py-2 text-center">No additional locations available</div>
              )}
            </div>
          </div>
        </div>
        
        {/* Close button */}
        <div className="flex justify-end mt-4 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-6 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm" >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerViewModal;
